import { useState } from 'react';

import { useScrapingIntents } from '../../model/use-scraping-intents';
import { ScrapingPagination } from '../../ui/scraping-pagination';
import {
  ScrapingEmptyState,
  ScrapingErrorState,
  ScrapingTableLoadingState,
} from '../../ui/scraping-section-state';
import { ScrapingIntentTable } from '../scraping-intent/scraping-intent-table';
import { SCRAPING_INTENT_MODE, SCRAPING_INTENT_SELECTOR_TYPE } from '../../types';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter';
import { Stage } from '@/shared/components/stage/stage';
import { PlusIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { DirectUrlScrapingDialog } from './direct-url-scraping.dialog';
import { Button } from '@/shared/components/shadcn/button';

export function DirectUrlScrapingList() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack.at(-1);
  const [mode, setMode] = useState<undefined | SCRAPING_INTENT_MODE>(undefined);

  const intentsQuery = useScrapingIntents({
    type: SCRAPING_INTENT_SELECTOR_TYPE.URLS,
    limit: 10,
    cursor,
    platform: SOURCE_PLATFORM_TYPE.TIKTOK,
    mode: mode,
  });

  const nextCursor = intentsQuery.data?.page.next_cursor;

  const handleCreateNew = () => {
    overlay.open(({ isOpen, close, unmount }) => {
      return (
        <DirectUrlScrapingDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={() => {}}
          onExit={unmount}
        />
      );
    });
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-6">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-xl font-semibold">
            Direct URL Scraping list ({intentsQuery.data?.page.total_count || 0})
          </p>
          <p className="text-muted-foreground text-sm">
            View and manage your URL-based scraping requests. Track the progress of each request and
            stay updated.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={handleCreateNew} variant="default">
            <PlusIcon />
            Create new
          </Button>
        </div>
      </div>
      <Stage>
        <Stage.Scene status={intentsQuery.isPending}>
          <ScrapingTableLoadingState />
        </Stage.Scene>
        <Stage.Scene status={intentsQuery.isError}>
          <ScrapingErrorState onRetry={() => void intentsQuery.refetch()} />
        </Stage.Scene>
        <Stage.Scene status={intentsQuery.data?.items.length === 0}>
          <ScrapingEmptyState />
        </Stage.Scene>
        <Stage.Scene default>
          <ScrapingIntentTable
            intents={intentsQuery.data?.items || []}
            kind="direct-url"
            mode={mode}
            setMode={setMode}
          />
        </Stage.Scene>
      </Stage>

      <ScrapingPagination
        hasMore={!!intentsQuery.data?.page.has_more}
        canGoBack={cursorStack.length > 0}
        isFetching={intentsQuery.isFetching}
        onNext={() => {
          if (nextCursor != null) {
            setCursorStack((previous) => [...previous, nextCursor]);
          }
        }}
        onBack={() => setCursorStack((previous) => previous.slice(0, -1))}
      />
    </section>
  );
}
